import { useEffect, useMemo, useState } from 'react';
import { NavLink, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useLiveQuery } from 'dexie-react-hooks';
import { useDispatch, useSelector } from 'react-redux';
import AppBar from '@mui/material/AppBar';
import Avatar from '@mui/material/Avatar';
import Badge from '@mui/material/Badge';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import GlobalStyles from '@mui/material/GlobalStyles';
import IconButton from '@mui/material/IconButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Switch from '@mui/material/Switch';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import TaskIcon from '@mui/icons-material/esm/Assignment';
import DefectIcon from '@mui/icons-material/esm/BugReport';
import QcIcon from '@mui/icons-material/esm/Checklist';
import DfrIcon from '@mui/icons-material/esm/Article';
import TimesheetIcon from '@mui/icons-material/esm/AccessTime';
import AttendanceIcon from '@mui/icons-material/esm/Badge';
import ExitFieldIcon from '@mui/icons-material/esm/ExitToApp';
import HomeIcon from '@mui/icons-material/esm/Home';
import LogoutIcon from '@mui/icons-material/esm/Logout';
import SyncIcon from '@mui/icons-material/esm/Sync';
import LockResetIcon from '@mui/icons-material/esm/LockReset';
import DarkModeIcon from '@mui/icons-material/esm/DarkMode';
import LightModeIcon from '@mui/icons-material/esm/LightMode';
import MenuIcon from '@mui/icons-material/esm/Menu';
import MenuOpenIcon from '@mui/icons-material/esm/MenuOpen';
import EmergencyIcon from '@mui/icons-material/esm/ContactEmergency';
import InspectionIcon from '@mui/icons-material/esm/FactCheck';
import DispatchIcon from '@mui/icons-material/esm/LocalShipping';
import AccountsIcon from '@mui/icons-material/esm/AccountBalance';
import ProcurementIcon from '@mui/icons-material/esm/HomeWork';
import StoresIcon from '@mui/icons-material/esm/Storefront';
import WorkersIcon from '@mui/icons-material/esm/Engineering';
import ApprovalIcon from '@mui/icons-material/esm/Approval';
import TransactionsIcon from '@mui/icons-material/esm/ReceiptLong';
import ClosePoIcon from '@mui/icons-material/esm/TaskAlt';
import OrdersIcon from '@mui/icons-material/esm/ShoppingCartCheckout';
import RiskIcon from '@mui/icons-material/esm/WarningAmber';
import SuppliersIcon from '@mui/icons-material/esm/Groups';
import ReceivingIcon from '@mui/icons-material/esm/MoveToInbox';
import PickingIcon from '@mui/icons-material/esm/Inventory';
import StockIcon from '@mui/icons-material/esm/Warehouse';
import NotificationsIcon from '@mui/icons-material/esm/Notifications';
import HistoryIcon from '@mui/icons-material/esm/ManageHistory';
import { AppDispatch, RootState } from '../../store';
import { logout } from '../../store/slices/authSlice';
import { fetchProjects, setActiveProject } from '../../store/slices/projectSlice';
import { fieldDb } from '../../services/offline/fieldDb';
import fieldSyncService from '../../services/offline/fieldSyncService';
import notificationService, { NotificationListItem } from '../../services/api/notificationService';
import {
  adminFieldViewRoles,
  canUseAdminFieldViewSwitcherForUser,
  effectiveFieldRoleForUser,
  isFieldOnlyRole,
  normalizeRole,
} from '../../routes/fieldAccess';
import { fieldNavigationForRole, FieldNavIcon } from '../../routes/fieldNavigation';
import fairmontLogo from '../../assets/branding/fairmont-estate-logo-mono-cream-1024.png';

interface FieldModeLayoutProps {
  children: React.ReactNode;
}

const fieldNavIcons: Record<FieldNavIcon, JSX.Element> = {
  home: <HomeIcon />, task: <TaskIcon />, workers: <WorkersIcon />, attendance: <AttendanceIcon />, time: <TimesheetIcon />, qc: <QcIcon />, defect: <DefectIcon />,
  inspection: <InspectionIcon />, dfr: <DfrIcon />, dispatch: <DispatchIcon />, emergency: <EmergencyIcon />,
  accounts: <AccountsIcon />, approval: <ApprovalIcon />, transactions: <TransactionsIcon />, close: <ClosePoIcon />,
  procurement: <ProcurementIcon />, orders: <OrdersIcon />, risk: <RiskIcon />, suppliers: <SuppliersIcon />,
  stores: <StoresIcon />, receiving: <ReceivingIcon />, picking: <PickingIcon />, stock: <StockIcon />,
  history: <HistoryIcon />,
};

export const fieldNavItemsForRole = (role?: string) => fieldNavigationForRole(role).map((item) => ({
  ...item,
  icon: fieldNavIcons[item.icon],
}));

const workerReviewRouteFor = (path: string, workerBasePath: string | null) => {
  if (!workerBasePath) return path;
  if (path === '/field') return workerBasePath;
  if (path === '/field/tasks') return `${workerBasePath}/current`;
  if (path === '/field/upcoming') return `${workerBasePath}/upcoming`;
  if (path === '/field/performance') return `${workerBasePath}/performance`;
  if (path === '/field/worker/emergency') return `${workerBasePath}/emergency`;
  return path;
};

const initialsFor = (first?: string, last?: string) =>
  `${first?.charAt(0) ?? ''}${last?.charAt(0) ?? ''}`.trim() || 'FM';

const FIELD_DARK_MODE_KEY = 'fieldModeDarkPreview';
const FIELD_SIDE_PANEL_KEY = 'fieldModeSidePanelOpen';

const notificationHintFor = (notification: NotificationListItem) => {
  if (notification.notificationType === 'task_ready_for_inspection') {
    return notification.title.toLowerCase().includes('rework') ? 'Rework done' : 'New task needs inspecting';
  }
  if (notification.notificationType === 'task_inspection_failed') return 'Task needs rework';
  if (notification.notificationType === 'task_inspection_approved') return 'Task approved';
  if (notification.notificationType === 'field_report_submitted') return 'New field report submitted';
  if (notification.notificationType.includes('payment') || notification.notificationType.includes('transaction')) return 'Payment activity';
  if (notification.notificationType.includes('purchase_order') || notification.notificationType.includes('po')) return 'Purchase order activity';
  return notification.title;
};

export default function FieldModeLayout({ children }: FieldModeLayoutProps) {
  const [isOnline, setIsOnline] = useState(() => navigator.onLine);
  const [syncing, setSyncing] = useState(false);
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const [notificationHints, setNotificationHints] = useState<string[]>([]);
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem(FIELD_DARK_MODE_KEY) === 'true');
  const [sidePanelOpen, setSidePanelOpen] = useState(() => localStorage.getItem(FIELD_SIDE_PANEL_KEY) !== 'false');
  const [profileMenuAnchor, setProfileMenuAnchor] = useState<HTMLElement | null>(null);
  const dispatch = useDispatch() as AppDispatch;
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { user } = useSelector((state: RootState) => state.auth);
  const { projects, activeProjectId } = useSelector((state: RootState) => state.projects);
  const userRole = normalizeRole(user?.role);
  const adminFieldViewer = canUseAdminFieldViewSwitcherForUser(userRole, user?.email);
  const effectiveFieldRole = effectiveFieldRoleForUser(user?.role, searchParams.get('view'), location.pathname, user?.email);
  const fieldNavItems = useMemo(() => fieldNavItemsForRole(effectiveFieldRole), [effectiveFieldRole]);
  const workerReviewBasePath = useMemo(() => {
    const match = location.pathname.match(/^\/field\/workers\/([^/]+)/);
    return match?.[1] ? `/field/workers/${match[1]}` : null;
  }, [location.pathname]);
  const resolvedFieldNavItems = useMemo(
    () => fieldNavItems.map((item) => ({
      ...item,
      path: workerReviewRouteFor(item.path, workerReviewBasePath),
    })),
    [fieldNavItems, workerReviewBasePath]
  );
  const fieldOnly = isFieldOnlyRole(user?.role);
  const commercialFieldMode = ['accounts', 'procurement', 'stores'].includes(effectiveFieldRole);
  const adminPreviewParam = adminFieldViewer ? `view=${encodeURIComponent(effectiveFieldRole)}` : '';
  const navTargetFor = (path: string) =>
    adminPreviewParam && path.startsWith('/field') ? `${path}${path.includes('?') ? '&' : '?'}${adminPreviewParam}` : path;
  const pendingOutboxCount = useLiveQuery(
    () => fieldDb.fieldOutbox.where('status').equals('pending').count(),
    [],
    0
  );
  const failedOutboxCount = useLiveQuery(
    () => fieldDb.fieldOutbox.where('status').equals('failed').count(),
    [],
    0
  );
  const conflictOutboxCount = useLiveQuery(
    () => fieldDb.fieldOutbox.where('status').equals('conflict').count(),
    [],
    0
  );
  const lastPulledAt = useLiveQuery(
    () => fieldDb.syncMetadata.get('lastPulledAt'),
    [],
    undefined
  );
  const totalAttentionCount = pendingOutboxCount + failedOutboxCount + conflictOutboxCount;

  useEffect(() => {
    dispatch(fetchProjects());
  }, [dispatch]);

  useEffect(() => {
    if (!projects.length) return;
    const activeProjectIsVisible = projects.some((project) => project.id === activeProjectId);
    if (!activeProjectIsVisible) {
      dispatch(setActiveProject(projects[0].id));
    }
  }, [activeProjectId, dispatch, projects]);

  useEffect(() => {
    const setOnline = () => setIsOnline(true);
    const setOffline = () => setIsOnline(false);
    window.addEventListener('online', setOnline);
    window.addEventListener('offline', setOffline);
    return () => {
      window.removeEventListener('online', setOnline);
      window.removeEventListener('offline', setOffline);
    };
  }, []);

  const activeItem = useMemo(
    () => {
      const navPath = (item: { path: string }) => item.path.split('?')[0];
      const exact = resolvedFieldNavItems.find((item) => location.pathname === navPath(item));
      if (exact) return exact;
      return [...resolvedFieldNavItems]
        .sort((a, b) => b.path.length - a.path.length)
        .find((item) => location.pathname.startsWith(`${navPath(item)}/`));
    },
    [resolvedFieldNavItems, location.pathname]
  );

  const handleLogout = async () => {
    setProfileMenuAnchor(null);
    await dispatch(logout());
    navigate('/login');
  };

  const handleExitFieldMode = () => navigate('/dashboard');

  const loadNotificationCount = async () => {
    try {
      const center = await notificationService.getCenter({ readState: 'unread', limit: 3 });
      setUnreadNotifications(center.totals?.unread ?? 0);
      setNotificationHints(center.notifications.slice(0, 3).map(notificationHintFor));
    } catch {
      setUnreadNotifications(0);
      setNotificationHints([]);
    }
  };

  const pullFieldCache = async () => {
    if (!navigator.onLine || syncing) return;
    try {
      setSyncing(true);
      await fieldSyncService.syncNow();
    } catch {
      // Sync failures leave the existing Dexie cache and retryable outbox rows intact.
    } finally {
      setSyncing(false);
    }
  };

  useEffect(() => {
    if (isOnline) {
      pullFieldCache();
      loadNotificationCount();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOnline]);

  useEffect(() => {
    loadNotificationCount();
    const interval = window.setInterval(loadNotificationCount, 60000);
    return () => window.clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const toggleNotifications = () => {
    if (location.pathname === '/field/notifications') {
      navigate('/field');
      return;
    }
    navigate('/field/notifications');
  };

  const toggleDarkMode = () => {
    setDarkMode((current) => {
      const next = !current;
      localStorage.setItem(FIELD_DARK_MODE_KEY, String(next));
      return next;
    });
  };

  const toggleSidePanel = () => {
    setSidePanelOpen((current) => {
      const next = !current;
      localStorage.setItem(FIELD_SIDE_PANEL_KEY, String(next));
      return next;
    });
  };

  const handleAdminViewChange = (view: string) => {
    const nextParams = new URLSearchParams();
    nextParams.set('view', view);
    navigate(`/field?${nextParams.toString()}`);
  };

  const palette = darkMode
    ? {
      page: '#131210',
      text: '#f3f0e8',
      muted: '#aaa297',
      sidebar: '#0b0b0a',
      sidebarHover: '#1d1b18',
      selected: '#765f32',
      appbar: '#1a1815',
      appbarBorder: '#38332c',
      panel: '#1f1c18',
      panelAlt: '#28241f',
      panelBorder: '#3b352d',
      input: '#171512',
    }
    : {
      page: '#f6f5f3',
      text: '#1f1f1d',
      muted: '#6f6f6b',
      sidebar: '#181816',
      sidebarHover: '#2a2926',
      selected: '#20201f',
      appbar: '#ffffff',
      appbarBorder: '#e8e5e1',
      panel: '#ffffff',
      panelAlt: '#f4f2ef',
      panelBorder: '#e8e5e1',
      input: '#ffffff',
    };

  return (
    <Box
      sx={{
        display: 'flex',
        minHeight: '100vh',
        bgcolor: palette.page,
        color: palette.text,
        transition: 'background-color 160ms ease, color 160ms ease',
      }}
    >
      {darkMode && (
        <GlobalStyles
          styles={{
            '.MuiDialog-paper, .MuiPopover-paper, .MuiMenu-paper': {
              backgroundColor: `${palette.panel} !important`,
              color: `${palette.text} !important`,
              borderColor: `${palette.panelBorder} !important`,
            },
            '.MuiDialog-paper .MuiTypography-root, .MuiPopover-paper .MuiTypography-root, .MuiMenu-paper .MuiTypography-root': {
              color: `${palette.text} !important`,
            },
            '.MuiDialog-paper *:not(.MuiChip-root):not(.MuiChip-root *):not(.MuiAlert-root):not(.MuiAlert-root *), .MuiPopover-paper *:not(.MuiChip-root):not(.MuiChip-root *):not(.MuiAlert-root):not(.MuiAlert-root *), .MuiMenu-paper *:not(.MuiChip-root):not(.MuiChip-root *)': {
              color: `${palette.text} !important`,
            },
            '.MuiMenuItem-root, .MuiAutocomplete-option': {
              color: `${palette.text} !important`,
            },
            '.MuiMenuItem-root:hover, .MuiAutocomplete-option:hover, .MuiAutocomplete-option.Mui-focused': {
              backgroundColor: `${palette.panelAlt} !important`,
            },
            '.MuiDialog-paper .MuiInputBase-root, .MuiPopover-paper .MuiInputBase-root': {
              backgroundColor: `${palette.input} !important`,
              color: `${palette.text} !important`,
            },
            '.MuiDialog-paper .MuiOutlinedInput-notchedOutline, .MuiPopover-paper .MuiOutlinedInput-notchedOutline': {
              borderColor: `${palette.panelBorder} !important`,
            },
            '.MuiDialog-paper .MuiInputLabel-root, .MuiPopover-paper .MuiInputLabel-root, .MuiDialog-paper .MuiFormHelperText-root': {
              color: `${palette.muted} !important`,
            },
            '.MuiDialog-paper .MuiTypography-colorTextSecondary, .MuiPopover-paper .MuiTypography-colorTextSecondary, .MuiDialog-paper .MuiTypography-colorText\\.secondary, .MuiPopover-paper .MuiTypography-colorText\\.secondary': {
              color: `${palette.muted} !important`,
            },
            '.MuiDialog-paper .MuiButton-contained, .MuiDialog-paper .MuiButton-contained *, .MuiPopover-paper .MuiButton-contained, .MuiPopover-paper .MuiButton-contained *': {
              color: '#ffffff !important',
            },
            '.MuiChip-root.MuiChip-colorDefault, .MuiChip-root.MuiChip-outlined': {
              backgroundColor: `${palette.panelAlt} !important`,
              borderColor: `${palette.panelBorder} !important`,
              color: `${palette.text} !important`,
            },
            '.MuiChip-root.MuiChip-colorDefault .MuiChip-label, .MuiChip-root.MuiChip-outlined .MuiChip-label, .MuiChip-root.MuiChip-colorDefault .MuiChip-icon, .MuiChip-root.MuiChip-outlined .MuiChip-icon': {
              color: `${palette.text} !important`,
            },
          }}
        />
      )}
      <Box
        component="aside"
        sx={{
          width: { sm: sidePanelOpen ? 248 : 72 },
          flex: '0 0 auto',
          minHeight: '100vh',
          height: '100vh',
          position: 'sticky',
          top: 0,
          display: { xs: 'none', sm: 'flex' },
          flexDirection: 'column',
          bgcolor: palette.sidebar,
          color: 'common.white',
          transition: 'width 180ms ease',
          overflow: 'hidden',
        }}
      >
        <Stack
          direction={sidePanelOpen ? 'row' : 'column'}
          alignItems="center"
          justifyContent={sidePanelOpen ? 'space-between' : 'center'}
          spacing={sidePanelOpen ? 0 : 1}
          sx={{ px: sidePanelOpen ? 1.5 : 1, py: 1.25, minHeight: sidePanelOpen ? 64 : 100 }}
        >
          <Box
            sx={{
              width: sidePanelOpen ? 56 : 44,
              height: sidePanelOpen ? 56 : 44,
              borderRadius: 1,
              overflow: 'visible',
              bgcolor: 'transparent',
              boxShadow: 'none',
            }}
          >
            <Box
              component="img"
              src={fairmontLogo}
              alt="Fairmont Estate"
              sx={{ display: 'block', width: '100%', height: '100%', objectFit: 'contain' }}
            />
          </Box>
          <IconButton
            onClick={toggleSidePanel}
            aria-label={sidePanelOpen ? 'Close side panel' : 'Open side panel'}
            title={sidePanelOpen ? 'Close side panel' : 'Open side panel'}
            sx={{
              width: 44,
              height: 44,
              color: 'common.white',
              bgcolor: 'rgba(255,255,255,0.10)',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.18)' },
            }}
          >
            {sidePanelOpen ? <MenuOpenIcon /> : <MenuIcon />}
          </IconButton>
        </Stack>

        <Stack component="nav" spacing={0.5} sx={{ flex: 1, px: { sm: 1, md: 1.5 }, py: 1 }}>
          {resolvedFieldNavItems.map((item) => {
            const selected = activeItem?.path === item.path;
            return (
              <Button
                key={item.path}
                component={NavLink}
                to={navTargetFor(item.path)}
                startIcon={item.icon}
                aria-label={item.label}
                title={item.label}
                sx={{
                  minHeight: 48,
                  minWidth: 0,
                  width: '100%',
                  justifyContent: sidePanelOpen ? 'flex-start' : 'center',
                  gap: sidePanelOpen ? 1.5 : 0,
                  px: sidePanelOpen ? 1.75 : 1,
                  color: selected ? 'common.white' : 'rgba(255,255,255,0.86)',
                  position: 'relative',
                  overflow: 'hidden',
                  border: '1px solid',
                  borderColor: selected ? 'rgba(255,255,255,0.38)' : 'rgba(255,255,255,0)',
                  bgcolor: selected ? 'rgba(255,255,255,0.14)' : 'transparent',
                  backgroundImage: selected
                    ? 'linear-gradient(135deg, rgba(255,255,255,0.30), rgba(90,210,255,0.18) 44%, rgba(255,255,255,0.08))'
                    : 'linear-gradient(135deg, rgba(255,255,255,0), rgba(255,255,255,0))',
                  boxShadow: selected
                    ? 'inset 0 1px 0 rgba(255,255,255,0.48), 0 0 0 1px rgba(125,225,255,0.20), 0 10px 26px rgba(31,111,229,0.34), 0 0 24px rgba(125,225,255,0.28)'
                    : 'inset 0 1px 0 rgba(255,255,255,0), 0 0 0 rgba(0,0,0,0)',
                  backdropFilter: 'blur(10px)',
                  fontWeight: 700,
                  transform: 'translateZ(0)',
                  transition: 'background-color 180ms ease, background-image 180ms ease, border-color 180ms ease, box-shadow 180ms ease, color 180ms ease, transform 140ms ease',
                  '& .MuiButton-startIcon': { mr: sidePanelOpen ? 1 : 0, ml: 0 },
                  '& .MuiButton-startIcon svg': { fontSize: 22 },
                  '&:before': {
                    content: '""',
                    position: 'absolute',
                    inset: 1,
                    borderRadius: 'inherit',
                    pointerEvents: 'none',
                    background: selected
                      ? 'linear-gradient(120deg, rgba(255,255,255,0.34), rgba(255,255,255,0.04) 42%, rgba(125,225,255,0.16))'
                      : 'linear-gradient(120deg, rgba(255,255,255,0), rgba(255,255,255,0))',
                    opacity: selected ? 1 : 0,
                    transition: 'opacity 180ms ease, background 180ms ease',
                  },
                  '&:hover': {
                    color: 'common.white',
                    bgcolor: 'rgba(255,255,255,0.13)',
                    borderColor: 'rgba(255,255,255,0.32)',
                    backgroundImage: 'linear-gradient(135deg, rgba(255,255,255,0.26), rgba(90,210,255,0.16) 46%, rgba(255,255,255,0.08))',
                    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.42), 0 0 0 1px rgba(125,225,255,0.16), 0 10px 24px rgba(0,0,0,0.18), 0 0 22px rgba(125,225,255,0.24)',
                    transform: 'translateX(2px)',
                    '&:before': { opacity: 1 },
                  },
                  '&:active': {
                    transform: 'translateX(2px) scale(0.985)',
                    boxShadow: 'inset 0 2px 8px rgba(0,0,0,0.24), 0 0 0 1px rgba(125,225,255,0.22), 0 0 18px rgba(125,225,255,0.20)',
                  },
                  '& .MuiButton-startIcon, & span': {
                    position: 'relative',
                    zIndex: 1,
                  },
                }}
              >
                {sidePanelOpen && <Box component="span">{item.label}</Box>}
              </Button>
            );
          })}
        </Stack>

        <Stack
          direction="column"
          spacing={1}
          alignItems="center"
          sx={{ p: sidePanelOpen ? 1.5 : 1, borderTop: '1px solid rgba(255,255,255,0.12)' }}
        >
          <Button
            onClick={handleLogout}
            startIcon={<LogoutIcon />}
            aria-label="Logout"
            title="Logout"
            sx={{
              minHeight: 44,
              minWidth: 0,
              width: '100%',
              justifyContent: sidePanelOpen ? 'flex-start' : 'center',
              px: sidePanelOpen ? 1.5 : 1,
              color: 'rgba(255,255,255,0.86)',
              bgcolor: 'rgba(255,255,255,0.06)',
              border: '1px solid rgba(255,255,255,0.08)',
              '& .MuiButton-startIcon': { mr: sidePanelOpen ? 1 : 0, ml: 0 },
              '&:hover': {
                bgcolor: 'rgba(255,255,255,0.12)',
                color: 'common.white',
              },
            }}
          >
            {sidePanelOpen && 'Logout'}
          </Button>
        </Stack>
      </Box>

      <Box sx={{ minWidth: 0, flex: 1, pb: { xs: 9, sm: 0 } }}>
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          bgcolor: palette.appbar,
          color: palette.text,
          borderBottom: `1px solid ${palette.appbarBorder}`,
        }}
      >
        <Toolbar
          sx={{
            minHeight: { xs: 64, md: 72 },
            gap: 1.5,
            px: { xs: 1.5, md: 3 },
          }}
        >
          <Box sx={{ flexGrow: 1, minWidth: 0 }} />

          {adminFieldViewer && (
            <Stack
              direction="row"
              alignItems="center"
              spacing={0.75}
              sx={{
                display: { xs: 'none', lg: 'flex' },
                px: 1,
                py: 0.5,
                borderRadius: 1,
                border: `1px solid ${palette.panelBorder}`,
                bgcolor: palette.panelAlt,
              }}
            >
              <Typography sx={{ fontSize: 12, fontWeight: 900, color: palette.muted, textTransform: 'uppercase' }}>
                Viewing
              </Typography>
              {adminFieldViewRoles.map((view) => {
                const selected = effectiveFieldRole === view;
                return (
                  <Button
                    key={view}
                    size="small"
                    variant={selected ? 'contained' : 'text'}
                    onClick={() => handleAdminViewChange(view)}
                    sx={{
                      minHeight: 34,
                      px: 1.25,
                      borderRadius: 1,
                      fontSize: 12,
                      fontWeight: 900,
                      textTransform: 'capitalize',
                      bgcolor: selected ? palette.selected : 'transparent',
                      color: selected ? 'common.white' : palette.text,
                      '&:hover': {
                        bgcolor: selected ? palette.selected : palette.panel,
                      },
                    }}
                  >
                    {view}
                  </Button>
                );
              })}
            </Stack>
          )}

          <Stack
            direction="row"
            alignItems="center"
            spacing={0.5}
            sx={{
              px: 1,
              py: 0.25,
              borderRadius: 999,
              border: `1px solid ${palette.panelBorder}`,
              bgcolor: darkMode ? '#171512' : '#f4f2ef',
            }}
          >
            {darkMode ? <DarkModeIcon fontSize="small" /> : <LightModeIcon fontSize="small" />}
            <Switch
              size="small"
              checked={darkMode}
              onChange={toggleDarkMode}
              inputProps={{ 'aria-label': 'Toggle dark mode preview' }}
            />
          </Stack>

          <Tooltip
            arrow
            title={
              notificationHints.length ? (
                <Stack spacing={0.5}>
                  {notificationHints.map((hint, index) => (
                    <Typography key={`${hint}-${index}`} variant="caption" sx={{ color: 'inherit', fontWeight: 800 }}>
                      {hint}
                    </Typography>
                  ))}
                </Stack>
              ) : 'Notifications'
            }
          >
            <Badge badgeContent={unreadNotifications} color="error">
              <IconButton
                onClick={toggleNotifications}
                aria-label={notificationHints[0] ?? 'Notifications'}
                sx={{
                  width: 46,
                  height: 46,
                  borderRadius: '50%',
                  border: `1px solid ${location.pathname === '/field/notifications' ? palette.selected : palette.panelBorder}`,
                  bgcolor: location.pathname === '/field/notifications'
                    ? palette.selected
                    : darkMode
                      ? '#171512'
                      : '#f4f2ef',
                  color: location.pathname === '/field/notifications'
                    ? 'common.white'
                    : palette.text,
                  boxShadow: location.pathname === '/field/notifications'
                    ? `0 10px 24px ${darkMode ? 'rgba(230,204,148,0.22)' : 'rgba(32,29,24,0.14)'}`
                    : 'none',
                  transition: 'background-color 160ms ease, border-color 160ms ease, transform 160ms ease, box-shadow 160ms ease',
                  '&:hover': {
                    bgcolor: location.pathname === '/field/notifications' ? palette.selected : palette.panel,
                    transform: 'translateY(-1px)',
                    boxShadow: darkMode ? '0 10px 24px rgba(0,0,0,0.28)' : '0 10px 24px rgba(31,31,29,0.12)',
                  },
                  '&:active': { transform: 'translateY(0)' },
                }}
              >
                <NotificationsIcon />
              </IconButton>
            </Badge>
          </Tooltip>

          <Chip
            label={
              conflictOutboxCount
                ? `${conflictOutboxCount} conflicts`
                : failedOutboxCount
                  ? `${failedOutboxCount} failed`
                  : pendingOutboxCount
                    ? `${pendingOutboxCount} pending`
                    : lastPulledAt?.value
                      ? `Synced ${new Date(lastPulledAt.value).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
                      : 'Synced'
            }
            color={conflictOutboxCount || failedOutboxCount ? 'warning' : pendingOutboxCount ? 'info' : 'success'}
            sx={{
              display: { xs: 'none', sm: 'inline-flex' },
              minHeight: 38,
              bgcolor: conflictOutboxCount || failedOutboxCount ? undefined : pendingOutboxCount ? undefined : '#2b8a3e',
              color: conflictOutboxCount || failedOutboxCount || pendingOutboxCount ? undefined : 'common.white',
              '& .MuiChip-label': { fontWeight: 700 },
            }}
          />

          <Badge badgeContent={totalAttentionCount} color="warning">
            <IconButton
              color="default"
              aria-label="sync status"
              onClick={pullFieldCache}
              disabled={!isOnline || syncing}
              sx={{ minWidth: 48, minHeight: 48 }}
            >
              <SyncIcon />
            </IconButton>
          </Badge>

          {!fieldOnly && (
            <>
              <IconButton
                onClick={handleExitFieldMode}
                color="default"
                aria-label="Back to dashboard"
                title="Back to dashboard"
                sx={{ minWidth: 48, minHeight: 48 }}
              >
                <ExitFieldIcon />
              </IconButton>

              <IconButton
                onClick={() => navigate('/field/change-password')}
                color="default"
                aria-label="Change password"
                title="Change password"
                sx={{ minWidth: 48, minHeight: 48 }}
              >
                <LockResetIcon />
              </IconButton>
            </>
          )}

          <IconButton
            onClick={handleLogout}
            color="default"
            aria-label="logout"
            sx={{ display: { xs: 'inline-flex', sm: 'none' }, minWidth: 48, minHeight: 48 }}
          >
            <LogoutIcon />
          </IconButton>
        </Toolbar>

        <Toolbar
          sx={{
            display: { xs: 'flex', lg: 'none' },
            gap: 1.5,
            minHeight: 56,
            px: 1.5,
            bgcolor: palette.panelAlt,
            borderTop: `1px solid ${palette.panelBorder}`,
          }}
        >
          <IconButton
            onClick={(event) => setProfileMenuAnchor(event.currentTarget)}
            aria-label="Open account menu"
            title="Account menu"
            sx={{ p: 0 }}
          >
            <Avatar sx={{ width: 36, height: 36, bgcolor: '#f5c542', color: '#172018', fontWeight: 800 }}>
              {initialsFor(user?.firstName, user?.lastName)}
            </Avatar>
          </IconButton>
          {adminFieldViewer && (
            <Stack direction="row" spacing={0.75} sx={{ overflowX: 'auto', flex: 1 }}>
              {adminFieldViewRoles.map((view) => {
                const selected = effectiveFieldRole === view;
                return (
                  <Button
                    key={view}
                    size="small"
                    variant={selected ? 'contained' : 'outlined'}
                    onClick={() => handleAdminViewChange(view)}
                    sx={{
                      flex: '0 0 auto',
                      minHeight: 36,
                      px: 1.1,
                      borderRadius: 1,
                      fontSize: 11,
                      fontWeight: 900,
                      textTransform: 'capitalize',
                      bgcolor: selected ? palette.selected : palette.panel,
                      color: selected ? 'common.white' : palette.text,
                      borderColor: palette.panelBorder,
                    }}
                  >
                    {view}
                  </Button>
                );
              })}
            </Stack>
          )}
        </Toolbar>
      </AppBar>

      <Menu
        anchorEl={profileMenuAnchor}
        open={Boolean(profileMenuAnchor)}
        onClose={() => setProfileMenuAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem disabled>
          <Box>
            <Typography variant="subtitle2">{user ? `${user.firstName} ${user.lastName}` : 'Field user'}</Typography>
            <Typography variant="caption" color="text.secondary">
              {adminFieldViewer ? `Viewing ${effectiveFieldRole.replace(/_/g, ' ')}` : user?.role?.replace(/_/g, ' ') ?? 'Field'}
            </Typography>
          </Box>
        </MenuItem>
        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          Logout
        </MenuItem>
      </Menu>

      <Stack
        direction="row"
        spacing={1}
        sx={{
          display: commercialFieldMode ? 'flex' : 'none',
          px: { sm: 2, md: 3 },
          py: 1,
          bgcolor: palette.panelAlt,
          borderBottom: `1px solid ${palette.panelBorder}`,
          overflowX: 'auto',
        }}
      >
        {resolvedFieldNavItems.map((item) => (
          <Button
            key={item.path}
            component={NavLink}
            to={navTargetFor(item.path)}
            startIcon={item.icon}
            variant={activeItem?.path === item.path ? 'contained' : 'outlined'}
            sx={{
              flex: '0 0 auto',
              minHeight: 40,
              px: 1.75,
              borderRadius: 999,
              fontWeight: 800,
              textTransform: 'none',
              bgcolor: activeItem?.path === item.path ? palette.selected : palette.panel,
              color: activeItem?.path === item.path ? 'common.white' : palette.text,
              borderColor: palette.panelBorder,
              '&:hover': {
                bgcolor: activeItem?.path === item.path ? palette.selected : palette.panelAlt,
              },
            }}
          >
            {item.label}
          </Button>
        ))}
      </Stack>

      <Box
        component="main"
        sx={{
          p: { xs: 1.5, sm: 3, md: 4 },
          maxWidth: 1184,
          mx: 'auto',
          color: palette.text,
          ...(darkMode
            ? {
              '& *:not(.MuiChip-root):not(.MuiChip-root *):not(.MuiAlert-root):not(.MuiAlert-root *)': {
                color: `${palette.text} !important`,
              },
              '& .MuiButton-contained, & .MuiButton-contained *, & .MuiChip-colorPrimary, & .MuiChip-colorPrimary *': {
                color: '#ffffff !important',
              },
              '& .MuiChip-root.MuiChip-colorDefault, & .MuiChip-root.MuiChip-outlined': {
                bgcolor: `${palette.panelAlt} !important`,
                borderColor: `${palette.panelBorder} !important`,
                color: `${palette.text} !important`,
              },
              '& .MuiChip-root.MuiChip-colorDefault .MuiChip-label, & .MuiChip-root.MuiChip-outlined .MuiChip-label, & .MuiChip-root.MuiChip-colorDefault .MuiChip-icon, & .MuiChip-root.MuiChip-outlined .MuiChip-icon': {
                color: `${palette.text} !important`,
              },
              '& .MuiTypography-colorTextSecondary, & .MuiTypography-colorText\\.secondary, & .MuiFormHelperText-root, & .MuiInputLabel-root': {
                color: `${palette.muted} !important`,
              },
            }
            : {}),
          '& .MuiPaper-root': {
            bgcolor: darkMode ? `${palette.panel} !important` : palette.panel,
            color: darkMode ? `${palette.text} !important` : palette.text,
            borderColor: darkMode ? `${palette.panelBorder} !important` : palette.panelBorder,
          },
          '& .MuiTableCell-root': {
            color: darkMode ? `${palette.text} !important` : palette.text,
            borderColor: darkMode ? `${palette.panelBorder} !important` : palette.panelBorder,
          },
          '& .MuiTypography-colorTextSecondary, & .MuiTypography-colorText\\.secondary': {
            color: darkMode ? `${palette.muted} !important` : palette.muted,
          },
          '& .MuiInputBase-root': {
            bgcolor: darkMode ? `${palette.input} !important` : palette.input,
            color: darkMode ? `${palette.text} !important` : palette.text,
          },
          '& .MuiInputLabel-root, & .MuiFormHelperText-root': {
            color: darkMode ? `${palette.muted} !important` : palette.muted,
          },
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: darkMode ? `${palette.panelBorder} !important` : palette.panelBorder,
          },
          '& .MuiAccordion-root': {
            bgcolor: darkMode ? `${palette.panel} !important` : palette.panel,
            color: darkMode ? `${palette.text} !important` : palette.text,
            borderColor: darkMode ? `${palette.panelBorder} !important` : palette.panelBorder,
          },
          '& .MuiAccordionSummary-root': {
            bgcolor: darkMode ? `${palette.panelAlt} !important` : undefined,
            color: darkMode ? `${palette.text} !important` : undefined,
          },
          '& .MuiAccordionDetails-root, & .MuiTableHead-root, & .MuiTableBody-root': {
            bgcolor: darkMode ? `${palette.panel} !important` : undefined,
            color: darkMode ? `${palette.text} !important` : undefined,
          },
          '& .MuiDialog-paper, & .MuiPopover-paper, & .MuiMenu-paper': {
            bgcolor: darkMode ? `${palette.panel} !important` : palette.panel,
            color: darkMode ? `${palette.text} !important` : palette.text,
          },
          '& .MuiMenuItem-root, & .MuiAutocomplete-option': {
            color: darkMode ? `${palette.text} !important` : undefined,
            '&:hover, &.Mui-focused': {
              bgcolor: darkMode ? `${palette.panelAlt} !important` : undefined,
            },
          },
          '& .MuiDivider-root': {
            borderColor: darkMode ? `${palette.panelBorder} !important` : undefined,
          },
          '& .MuiSelect-icon, & .MuiSvgIcon-root': {
            color: 'inherit',
          },
          '& .MuiButton-root, & .MuiIconButton-root, & .MuiInputBase-root': {
            minHeight: 48,
          },
          '& .MuiCard-root': {
            borderRadius: 2,
          },
        }}
      >
        {children}
      </Box>

      <Box
        component="nav"
        sx={{
          display: { xs: 'grid', md: 'none' },
          gridTemplateColumns: `repeat(${resolvedFieldNavItems.length}, 1fr)`,
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 1200,
          bgcolor: palette.sidebar,
          borderTop: '1px solid rgba(255,255,255,0.18)',
        }}
      >
        {resolvedFieldNavItems.map((item) => {
          const selected = activeItem?.path === item.path;
          return (
            <Button
              key={item.path}
              component={NavLink}
              to={item.path}
              sx={{
                minHeight: 64,
                borderRadius: 0,
                color: selected ? '#f5c542' : 'rgba(255,255,255,0.82)',
                display: 'flex',
                flexDirection: 'column',
                gap: 0.25,
                fontSize: 10,
                fontWeight: 800,
                lineHeight: 1.1,
                px: 0.25,
              }}
            >
              {item.icon}
              {item.label}
            </Button>
          );
        })}
      </Box>
      </Box>
    </Box>
  );
}
